import React from "react";
import Link from "next/link";
import Image from "next/image";
import HandDrawnBracket from "../atoms/svgs/HandDrawnBracket";

const RiskVsValueSection = () => {
	const comparisonRows = [
		{
			category: "Scope & Flexibility",
			themTitle: "Fixed bids. Rigid scopes.",
			themDescription: "Change requests pile up. Timelines slip.",
			craftedTitle: "No fixed-bid illusions.",
			craftedDescription: "Software changes. We adapt with you.",
			bracketColor: "blue",
		},
		{
			category: "Transparency",
			themTitle: "Limited visibility.",
			themDescription: "Status updates are vague. Budgets surface late.",
			craftedTitle: "Full transparency.",
			craftedDescription: "Weekly reports. Budget visibility. Ticket-level clarity.",
			bracketColor: "green" as const,
		},
		{
			category: "Ownership",
			themTitle: "Hidden dependencies.",
			themDescription: "Proprietary platforms. Hard-to-exit engagements.",
			craftedTitle: "You retain total ownership.",
			craftedDescription: "No platform lock-in, no dependency traps.",
			bracketColor: "red" as const,
		},
		{
			category: "Engagement Model",
			themTitle: "Big commitments up front.",
			themDescription: "Long contracts before trust is earned.",
			craftedTitle: "Start small. Scale when you're confident.",
			craftedDescription: "Kick off with a sprint before committing long term.",
			bracketColor: "yellow" as const,
		},
		{
			category: "Team Structure",
			themTitle: "A team you have to manage.",
			themDescription: "Misaligned roles. Junior staffing. Diffused accountability.",
			craftedTitle: "A team you don't have to manage.",
			craftedDescription:
				"Product, design, engineering, QA - all accountable, all senior, all aligned.",
			bracketColor: "blue" as const,
		},
	];

	return (
		<div className="RiskVsValueSection container mx-auto py-[clamp(40px,6.25vw,80px)] max-w-6xl px-2 xl:px-0">
			{/* Desktop Comparison Table */}
			<div className="mb-16 relative hidden md:block">
				{/* Vertical line separating category from "Them" column - spans full height */}
				<div className="absolute left-40 top-0 bottom-0 w-px bg-gray-200"></div>

				{/* Headers */}
				<div className="grid grid-cols-[10rem_1fr_1.3fr] gap-4 border-b border-gray-200 pb-4 relative">
					<div className=""></div>
					<div className="border-r border-gray-200">
						<h3 className="text-[clamp(16px,1.33vw,24px)] font-normal text-[#696969] italic">
							Them
						</h3>
					</div>
					<div className=" w-full h-full flex items-center justify-start">
						<div className="relative h-6 w-[108px]">
							<Image
								src="/images/crafted-logo-no-box.svg"
								alt="crafted"
								layout="fill"
								objectFit="contain"
								className="h-6"
							/>
						</div>
					</div>
				</div>

				{/* Rows */}
				{comparisonRows.map((row, index) => (
					<div key={index} className="grid grid-cols-[10rem_1fr_1.3fr] gap-4 relative">
						{/* Category Column */}
						<div className="font-bold py-6">{row.category}</div>

						{/* Them Column */}
						<div className="flex flex-col gap-1 border-r py-6 border-gray-200">
							<div
								className="text-[#696969] font-bold"
								dangerouslySetInnerHTML={{ __html: row.themTitle }}
							/>
							<div
								className="text-[#696969]"
								dangerouslySetInnerHTML={{ __html: row.themDescription }}
							/>
						</div>

						{/* Crafted Column with Bracket */}
						<div className="flex items-center h-full">
							<div className="relative flex items-stretch w-fit">
								<div className="flex-shrink-0">
									<HandDrawnBracket
										side="left"
										color={row.bracketColor}
										height="100%"
										className="h-full"
									/>
								</div>
								<div className="flex flex-col gap-1 py-1">
									<div
										className="flex-1 font-bold pl-5"
										dangerouslySetInnerHTML={{ __html: row.craftedTitle }}
									/>
									<div
										className="flex-1 pl-5"
										dangerouslySetInnerHTML={{ __html: row.craftedDescription }}
									/>
								</div>
								<div className="relative flex-shrink-0">
									<HandDrawnBracket
										side="right"
										color={row.bracketColor}
										height="100%"
										className="h-full"
									/>
								</div>
							</div>
						</div>
					</div>
				))}

				{/* Bottom border spanning full width */}
				<div className="border-b border-gray-200"></div>
			</div>

			{/* Mobile Card Layout */}
			<div className="mb-16 md:hidden space-y-6">
				{comparisonRows.map((row, index) => (
					<div
						key={index}
						className="border border-gray-200 rounded-lg p-6 space-y-6"
					>
						{/* Category Header */}
						<div className="font-bold text-lg pb-4 border-b border-gray-200">{row.category}</div>

						{/* Them Section */}
						<div className="space-y-2">
							<div className="text-xs font-medium text-[#696969] italic uppercase tracking-wide">
								Them
							</div>
							<div className="space-y-1">
								<div
									className="text-[#696969] font-bold"
									dangerouslySetInnerHTML={{ __html: row.themTitle }}
								/>
								<div
									className="text-[#696969]"
									dangerouslySetInnerHTML={{ __html: row.themDescription }}
								/>
							</div>
						</div>

						{/* Crafted Section with Brackets */}
						<div className="space-y-2 pt-4 border-t border-gray-100">
							<div className="text-xs font-medium text-gray-700 uppercase tracking-wide flex items-center gap-2">
								<div className="relative h-4 w-[72px]">
									<Image
										src="/images/crafted-logo-no-box.svg"
										alt="crafted"
										layout="fill"
										objectFit="contain"
										className="h-4"
									/>
								</div>
							</div>
							<div className="relative flex items-stretch w-full">
								<div className="flex-shrink-0">
									<HandDrawnBracket
										side="left"
										color={row.bracketColor}
										height={48}
										className="h-full"
									/>
								</div>
								<div className="flex flex-col gap-1 py-1 flex-1">
									<div
										className="font-bold pl-3"
										dangerouslySetInnerHTML={{ __html: row.craftedTitle }}
									/>
									<div
										className="pl-3"
										dangerouslySetInnerHTML={{ __html: row.craftedDescription }}
									/>
								</div>
								<div className="relative flex-shrink-0">
									<HandDrawnBracket
										side="right"
										color={row.bracketColor}
										height={48}
										className="h-full"
									/>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default RiskVsValueSection;
